import { useState, useCallback, useRef, useEffect } from 'react';
import { kbAutosuggest } from 'src/utilities/KBAutosuggestAPI';

const DEBOUNCE_MS = 500;
const MIN_PREFIX_LENGTH = 2;
const MAX_SUGGESTIONS = 20;

const useKBAutosuggest = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const onQueryChange = useCallback((rawPrefix) => {
    const prefix = typeof rawPrefix === 'string' ? rawPrefix.trim() : '';
    const currentRequestId = ++requestIdRef.current;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (prefix.length < MIN_PREFIX_LENGTH) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceTimerRef.current = setTimeout(() => {
      if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;

      const controller = new AbortController();
      abortControllerRef.current = controller;

      kbAutosuggest(prefix, MAX_SUGGESTIONS, { signal: controller.signal })
        .then((results) => {
          if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;
          setSuggestions(results.slice(0, MAX_SUGGESTIONS));
          setIsLoading(false);
          abortControllerRef.current = null;
        })
        .catch((error) => {
          if (!mountedRef.current || currentRequestId !== requestIdRef.current) return;
          if (error && error.name === 'AbortError') return;
          console.error('[OpenSearch autosuggest] error for prefix:', prefix, error);
          setSuggestions([]);
          setIsLoading(false);
          abortControllerRef.current = null;
        });
    }, DEBOUNCE_MS);
  }, []);

  return { suggestions, isLoading, onQueryChange };
};

export default useKBAutosuggest;
